import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { useForm, type FieldErrors, type Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDown, ChevronUp, Info, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldGroup, FormGrid } from "@/components/ui/section";
import { cn } from "@/lib/utils";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { ConfidenceStamp } from "./ConfidenceStamp";
import { projektProgress } from "./konzolaRows";
import { PartiesFields } from "./PartiesFields";
import { MapeFields } from "./MapeFields";
import {
  DEFAULT_PROJECT_DATA,
  NUMERIC_FIELD_NAMES,
  projectDataSchema,
  type ProjectDataInput,
  type ProjectDataValues,
} from "./schema";
import type { IfcDepth } from "./extractionMapper";
import {
  capNumericConfidenceForNL,
  mergePrefill,
} from "./mergePrefill";
import {
  useExtractionStore,
  type ExtractedField,
  type SourceKind,
} from "./useExtractionStore";

/** Imperative prefill surface the sources basket drives — the basket
 * lives outside the <form> (page level) but extraction results must
 * land in react-hook-form state, which only this component owns. */
export interface ProjectDataFormApi {
  applyExtraction: (
    values: Record<string, unknown>,
    ifc: IfcDepth,
    sourceKind: SourceKind,
  ) => void;
  applyNlExtraction: (
    extracted: Record<string, ExtractedField>,
    warnings: string[],
  ) => void;
  /** The opis card at the top of the page edits the form's
   * ``zahvat_opis`` field through here — the field itself is no longer
   * rendered inside the form, but it stays in form state so the
   * generate request carries it. */
  setZahvatOpis: (text: string) => void;
}

interface ProjectDataFormProps {
  initialValues?: Partial<ProjectDataInput>;
  onSubmit: (values: ProjectDataValues) => void;
  /** Debounced draft persistence — called with the raw (unvalidated)
   * form values on every change so a refresh or navigation can't lose
   * a half-filled form. */
  onDraftChange?: (values: Partial<ProjectDataInput>) => void;
  submitting?: boolean;
  /** DOM id for the form element so an external button (e.g. a sticky
   * action bar) can submit via the HTML ``form`` attribute. */
  formId?: string;
  /** Hide the in-form submit row — the page renders its own (sticky)
   * action bar wired through ``formId``. */
  externalSubmit?: boolean;
  /** Receives the prefill API on mount (see ProjectDataFormApi). */
  apiRef?: MutableRefObject<ProjectDataFormApi | null>;
  /** When true, render only fields whose extraction confidence is below
   * the review threshold — the "provjeri žute oznake" jump made real. */
  reviewFilter?: boolean;
  onClearReviewFilter?: () => void;
}

/** Confidence below this needs a human look (mirrors ConfidenceStamp). */
const REVIEW_THRESHOLD = 0.75;

interface SectionDef {
  id: string;
  title: string;
  fields: (keyof ProjectDataInput)[];
}

const SECTIONS: SectionDef[] = [
  {
    id: "project",
    title: "Broj, datum i lokacija",
    fields: ["project_number", "project_date", "project_location"],
  },
  {
    id: "investitor",
    title: "Investitor",
    fields: ["investitor_name", "investitor_address", "investitor_oib"],
  },
  {
    id: "katastar",
    title: "Građevna čestica",
    fields: ["katastarska_cestica", "katastarski_opcina", "cestica_povrsina_m2"],
  },
  {
    id: "ured",
    title: "Ured & projektant",
    fields: [
      "ured_name",
      "ured_address",
      "glavni_projektant_name",
      "glavni_projektant_title",
      "glavni_projektant_license",
      "direktor_name",
    ],
  },
  {
    id: "geometry",
    title: "Geometrija građevine",
    fields: [
      "katnost",
      "tlocrtna_projekcija_m2",
      "building_length_m",
      "building_width_m",
      "building_height_v_m",
      "building_height_V_m",
    ],
  },
  {
    id: "counts",
    title: "Brojevi & zelene površine",
    fields: ["num_stambenih", "num_poslovnih", "num_parking", "zelena_povrsina_m2"],
  },
  {
    id: "distances",
    title: "Udaljenosti od međa (opcionalno)",
    fields: [
      "udaljenost_sjever_m",
      "udaljenost_jug_m",
      "udaljenost_istok_m",
      "udaljenost_zapad_m",
      "regulacijski_pravac_m",
    ],
  },
  // S1 — Generalije: faza + strukovna odrednica as scalar inputs; the
  // section also hosts the MapeFields editor (popis mapa + zajednička
  // oznaka), so everything a real GP header needs lives in one place.
  { id: "mape", title: "Generalije", fields: ["faza", "strukovna_odrednica"] },
];

/** F2 — the form's sections grouped and ORDERED like the Donesi console's
 * groups, so step 1 and step 2 speak the same language in the same order:
 * ① Projekt podaci → ② Opći dio (ured/potpisi) → ③ Geometrija/Brojevi →
 * ④ Mape. Pure presentation — field names, validation and autosave are
 * untouched. */
const GROUPS: { id: string; title: string; sectionIds: string[] }[] = [
  {
    id: "projekt",
    title: "1 · Projekt (podaci)",
    sectionIds: ["project", "investitor", "katastar"],
  },
  {
    id: "opci-dio",
    title: "2 · Opći dio — ured i potpisi",
    sectionIds: ["ured"],
  },
  {
    id: "geometrija",
    title: "3 · Geometrija i brojevi",
    sectionIds: ["geometry", "counts", "distances"],
  },
  { id: "mape", title: "4 · Generalije", sectionIds: ["mape"] },
];

const FIELD_META: Record<
  string,
  { label: string; placeholder?: string; type?: "text" | "number" | "date"; full?: boolean; help?: string }
> = {
  project_number: { label: "Broj projekta" },
  project_date: { label: "Datum", type: "date" },
  project_location: { label: "Mjesto izrade", full: true },
  investitor_name: { label: "Naziv", full: true },
  investitor_address: { label: "Adresa", full: true },
  investitor_oib: { label: "OIB" },
  ured_name: { label: "Naziv ureda", full: true },
  ured_address: { label: "Adresa ureda", full: true },
  glavni_projektant_name: { label: "Glavni projektant" },
  glavni_projektant_title: { label: "Titula", placeholder: "ovl. arh." },
  glavni_projektant_license: { label: "Broj licence" },
  direktor_name: { label: "Direktor / odgovorna osoba" },
  katastarska_cestica: { label: "Katastarska čestica" },
  katastarski_opcina: { label: "Katastarska općina" },
  cestica_povrsina_m2: { label: "Površina čestice (m²)", type: "number" },
  katnost: { label: "Katnost", placeholder: "Po+P+1" },
  tlocrtna_projekcija_m2: { label: "Tlocrtna projekcija (m²)", type: "number" },
  building_length_m: { label: "Dužina (m)", type: "number" },
  building_width_m: { label: "Širina (m)", type: "number" },
  building_height_v_m: { label: "Visina v (m)", type: "number" },
  building_height_V_m: { label: "Ukupna visina V (m)", type: "number" },
  num_stambenih: { label: "Stambenih jedinica", type: "number" },
  num_poslovnih: { label: "Poslovnih jedinica", type: "number" },
  num_parking: { label: "Parkirnih mjesta", type: "number" },
  zelena_povrsina_m2: { label: "Zelena površina (m²)", type: "number" },
  udaljenost_sjever_m: { label: "Udaljenost sjever (m)", type: "number" },
  udaljenost_jug_m: { label: "Udaljenost jug (m)", type: "number" },
  udaljenost_istok_m: { label: "Udaljenost istok (m)", type: "number" },
  udaljenost_zapad_m: { label: "Udaljenost zapad (m)", type: "number" },
  regulacijski_pravac_m: { label: "Regulacijski pravac (m)", type: "number" },
  faza: { label: "Faza izrade", placeholder: "Glavni projekt" },
  strukovna_odrednica: {
    label: "Strukovna odrednica",
    placeholder: "Arhitektonski projekt",
  },
};

export function ProjectDataForm({
  initialValues,
  onSubmit,
  onDraftChange,
  submitting,
  formId,
  externalSubmit,
  apiRef,
  reviewFilter,
  onClearReviewFilter,
}: ProjectDataFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ProjectDataInput, unknown, ProjectDataValues>({
    resolver: zodResolver(projectDataSchema),
    defaultValues: { ...DEFAULT_PROJECT_DATA, ...initialValues } as ProjectDataInput,
  });

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // The whole data block collapses as one card — its header summary
  // (popunjeno / za provjeru) keeps the state readable while closed.
  const [groupOpen, setGroupOpen] = useState(true);
  const [lastPrefillBanner, setLastPrefillBanner] = useState<
    | { sourceKind: SourceKind; filled: number; skipped: number; warnings: string[] }
    | null
  >(null);

  function applyExtraction(
    values: Record<string, unknown>,
    ifc: IfcDepth,
    sourceKind: SourceKind,
  ) {
    for (const [k, v] of Object.entries(values)) {
      if (v == null) continue;
      // ``setValue`` accepts string keys; the resolver coerces.
      setValue(k as Path<ProjectDataInput>, v as never, { shouldDirty: true });
    }
    setValue("openings", ifc.openings, { shouldDirty: true });
    setValue("materials", ifc.materials, { shouldDirty: true });
    setValue("rooms_with_polygons", ifc.rooms_with_polygons, { shouldDirty: true });
    setValue("building_footprint_polygon", ifc.building_footprint_polygon, {
      shouldDirty: true,
    });
    setValue("extraction_warnings", ifc.extraction_warnings, { shouldDirty: true });
    // DXF/IFC path: ``Dropzone`` already wrote the store via
    // ``setExtraction``. Just remember the wave for the summary banner.
    setLastPrefillBanner({
      sourceKind,
      filled: Object.keys(values).length,
      skipped: 0,
      warnings: useExtractionStore.getState().warnings,
    });
  }

  /**
   * NL prefill path: ``NaturalLanguageInput`` hands us the raw extracted
   * payload. We enforce two policies here that don't apply to DXF/IFC:
   *
   *   1. Cap numeric confidence at 0.69 — the LLM can't be trusted to
   *      assert "120 m²" with high confidence because the value comes
   *      from a possibly-imprecise human description, not a measurement.
   *   2. Respect existing DXF/IFC stamps — a voice memo never overwrites
   *      a field that an engineering extractor already filled.
   */
  function applyNlExtraction(
    extracted: Record<string, ExtractedField>,
    warnings: string[],
  ) {
    const capped = capNumericConfidenceForNL(extracted, NUMERIC_FIELD_NAMES);
    const incomingValues: Record<string, unknown> = {};
    for (const [k, payload] of Object.entries(capped)) {
      if (payload.value != null && payload.value !== "") {
        incomingValues[k] = payload.value;
      }
    }
    const store = useExtractionStore.getState();
    const merge = mergePrefill(
      store.confidence,
      store.sourceKind,
      incomingValues,
      capped,
      "nl",
    );
    for (const [k, v] of Object.entries(merge.valuesToWrite)) {
      if (v == null) continue;
      setValue(k as Path<ProjectDataInput>, v as never, { shouldDirty: true });
    }
    useExtractionStore
      .getState()
      .setExtraction(merge.mergedConfidence, warnings, merge.resultSourceKind);
    setLastPrefillBanner({
      sourceKind: "nl",
      filled: Object.keys(merge.valuesToWrite).length,
      skipped: merge.skippedFields.length,
      warnings,
    });
  }

  // Reset entire form (used by Moji projekti switching).
  useEffect(() => {
    if (initialValues) {
      reset({ ...DEFAULT_PROJECT_DATA, ...initialValues } as ProjectDataInput);
    }
    // initialValues is set once at mount; we don't want a deep-equality
    // dance here. Reset is only triggered when consumer remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draft autosave — raw values, no validation gate; the zod resolver
  // only guards the submit path.
  const saveDraft = useDebouncedCallback((values: Partial<ProjectDataInput>) => {
    onDraftChange?.(values);
  }, 800);

  useEffect(() => {
    if (!onDraftChange) return;
    const sub = watch((values) => saveDraft(values as Partial<ProjectDataInput>));
    return () => sub.unsubscribe();
  }, [watch, saveDraft, onDraftChange]);

  const errorList = useMemo(() => flattenErrors(errors), [errors]);

  // Hand the prefill API to the page-level sources basket. Assigned on
  // every render so the closures always see fresh form state.
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      applyExtraction,
      applyNlExtraction,
      setZahvatOpis: (text) =>
        setValue("zahvat_opis", text, { shouldDirty: true }),
    };
    return () => {
      apiRef.current = null;
    };
  });

  // "Za provjeru" view — fields whose extraction stamp sits below the
  // review threshold. Driven by the status bar's jump, cleared inline.
  const confidence = useExtractionStore((s) => s.confidence);
  const reviewFields = useMemo(() => {
    const set = new Set<string>();
    for (const [field, meta] of Object.entries(confidence)) {
      if (!meta || meta.value == null || meta.value === "") continue;
      if (meta.confidence < REVIEW_THRESHOLD) set.add(field);
    }
    return set;
  }, [confidence]);

  const visibleSections = useMemo(() => {
    if (!reviewFilter) return SECTIONS;
    return SECTIONS.map((s) => ({
      ...s,
      fields: s.fields.filter((f) => reviewFields.has(f as string)),
    })).filter((s) => s.fields.length > 0);
  }, [reviewFilter, reviewFields]);

  // Header summary for the collapsed data block. ``watch()`` subscribes
  // the component to all values — fine at this form size, and it keeps
  // the count honest while typing. The progress counts the REQUIRED
  // fields (the console's ① Projekt rows — same source of truth), not
  // every input on the page: "12/30" told the architect nothing about
  // whether Generiraj would pass.
  const formValues = watch() as Record<string, unknown>;
  const progress = projektProgress(formValues);

  // Click on a summary-banner error → expand the section that owns the
  // field (an inline error inside a collapsed section is invisible) and
  // scroll/focus the input.
  function jumpToField(fieldName: string) {
    setGroupOpen(true);
    const section = SECTIONS.find((s) =>
      s.fields.includes(fieldName as keyof ProjectDataInput),
    );
    if (section) {
      setCollapsed((prev) => ({ ...prev, [section.id]: false }));
    }
    window.setTimeout(() => {
      const el = document.getElementById(fieldName);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    }, 60);
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      {lastPrefillBanner && (
        <PrefillSummary banner={lastPrefillBanner} onDismiss={() => setLastPrefillBanner(null)} />
      )}

      {reviewFilter && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-[0.85rem] text-amber-900">
          <span>
            Prikazana su samo polja za provjeru ({reviewFields.size}) — ostala
            su skrivena.
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onClearReviewFilter?.()}
          >
            Prikaži sva polja
          </Button>
        </div>
      )}

      {/* The whole data block is ONE collapsible card — its header carries
          the popunjeno/za-provjeru summary, the sections inside keep their
          individual toggles. */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => setGroupOpen((v) => !v)}
        >
          <CardTitle>Podaci projekta</CardTitle>
          <span className="ml-2 mr-auto flex flex-wrap items-center gap-2 text-[0.78rem] text-stone-500">
            <span
              className={cn(
                progress.missing.length === 0 && "font-medium text-green-700",
              )}
            >
              {progress.filled} od {progress.total} obaveznih popunjeno
            </span>
            {reviewFields.size > 0 && (
              <span className="font-medium text-amber-700">
                ⚠ {reviewFields.size} za provjeru
              </span>
            )}
          </span>
          <Button variant="ghost" size="sm" type="button">
            {!reviewFilter && !groupOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </Button>
        </CardHeader>
        {/* What's still missing, by name — visible even while the card is
            collapsed; each name jumps to (and focuses) its input. */}
        {progress.missing.length > 0 && (
          <div className="border-b border-stone-100 bg-stone-50/60 px-5 py-2 text-[0.78rem] text-stone-500">
            fali:{" "}
            {progress.missing.slice(0, 6).map((f, i) => (
              <span key={f.name}>
                {i > 0 && " · "}
                <button
                  type="button"
                  className="font-medium text-ind-700 underline decoration-ind-200 underline-offset-2 hover:decoration-ind-500"
                  onClick={() => jumpToField(f.name)}
                >
                  {f.label}
                </button>
              </span>
            ))}
            {progress.missing.length > 6 && (
              <span> … i još {progress.missing.length - 6}</span>
            )}
          </div>
        )}
        {(reviewFilter || groupOpen) && (
          <CardBody className="pt-2">
            {GROUPS.map((group) => {
              const sections = group.sectionIds
                .map((id) => visibleSections.find((s) => s.id === id))
                .filter((s): s is SectionDef => Boolean(s));
              if (sections.length === 0) return null;
              return (
                <div
                  key={group.id}
                  className="mt-2 border-t border-stone-200 pt-3 first:mt-0 first:border-0 first:pt-0"
                >
                  <h3 className="mb-1 text-[0.74rem] font-bold uppercase tracking-[0.04em] text-ind-700">
                    {group.title}
                  </h3>
                  {sections.map((section) => (
                    <section
                      key={section.id}
                      className="border-t border-stone-100 py-3 first:border-0 first:pt-1"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between text-left"
                        onClick={() =>
                          setCollapsed((prev) => ({
                            ...prev,
                            [section.id]: !prev[section.id],
                          }))
                        }
                      >
                        <h4 className="text-[0.78rem] font-semibold text-stone-600">
                          {section.title}
                        </h4>
                        {!reviewFilter && collapsed[section.id] ? (
                          <ChevronDown className="h-4 w-4 text-stone-400" />
                        ) : (
                          <ChevronUp className="h-4 w-4 text-stone-400" />
                        )}
                      </button>
                      {(reviewFilter || !collapsed[section.id]) && (
                        <div className="mt-3">
                          <FormGrid cols={2}>
                            {section.fields.map((fieldName) => {
                              const meta = FIELD_META[fieldName as string] ?? { label: fieldName };
                              const fieldErr = errors[fieldName] as { message?: string } | undefined;
                              return (
                                <FieldGroup key={fieldName} full={meta.full}>
                                  <div className="flex items-center justify-between">
                                    <Label htmlFor={fieldName}>{meta.label}</Label>
                                    <ConfidenceStamp fieldName={fieldName as string} />
                                  </div>
                                  <Input
                                    id={fieldName}
                                    type={meta.type ?? "text"}
                                    step={meta.type === "number" ? "any" : undefined}
                                    placeholder={meta.placeholder}
                                    className={cn(
                                      // A low-trust value must not blend into
                                      // the forest — the stamp says "provjeri",
                                      // the border keeps saying it on scroll.
                                      reviewFields.has(fieldName as string) &&
                                        "border-amber-400 bg-amber-50/40",
                                      fieldErr && "border-red-400 focus-visible:border-red-500",
                                    )}
                                    {...register(fieldName, {
                                      // #20 — "ručni unos briše oznaku":
                                      // a DOM change means the user typed
                                      // over the extracted value, so its
                                      // stamp no longer applies. setValue
                                      // prefills don't fire DOM onChange,
                                      // so fresh extraction stamps survive.
                                      onChange: () =>
                                        useExtractionStore
                                          .getState()
                                          .clearField(fieldName as string),
                                    })}
                                  />
                                  {fieldErr?.message && (
                                    <span className="mt-1 text-[0.72rem] text-red-700">
                                      {fieldErr.message}
                                    </span>
                                  )}
                                </FieldGroup>
                              );
                            })}
                          </FormGrid>
                          {/* R5–R7 — additional investitori / suradnici /
                              parcelacija live under the Investitor section
                              (the first investitor stays the scalar fields
                              above). */}
                          {section.id === "investitor" && !reviewFilter && (
                            <PartiesFields watch={watch} setValue={setValue} />
                          )}
                          {/* R8 — mape editor in its own ④ Mape group
                              (izrađivač/projektant records + zajednička
                              oznaka). */}
                          {section.id === "mape" && !reviewFilter && (
                            <MapeFields watch={watch} setValue={setValue} />
                          )}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              );
            })}
          </CardBody>
        )}
      </Card>

      {errorList.length > 0 && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700">
          <div className="font-semibold">Provjeri obavezna polja ({errorList.length}):</div>
          <ul className="mt-1 list-disc pl-5">
            {errorList.slice(0, 6).map((e) => (
              <li key={e.field}>
                <button
                  type="button"
                  className="underline decoration-red-300 underline-offset-2 hover:decoration-red-500"
                  onClick={() => jumpToField(e.field)}
                >
                  {FIELD_META[e.field]?.label ?? e.field}
                </button>
                : {e.message}
              </li>
            ))}
            {errorList.length > 6 && (
              <li className="list-none pt-1 text-[0.78rem] text-red-600">
                … i još {errorList.length - 6}
              </li>
            )}
          </ul>
        </div>
      )}

      {!externalSubmit && (
        <div className="flex items-center justify-end gap-3">
          <Button type="submit" disabled={submitting}>
            <Sparkles className="h-4 w-4" />
            {submitting ? "Generiram dokumente…" : "Generiraj dokumentaciju"}
          </Button>
        </div>
      )}
    </form>
  );
}

function flattenErrors(errors: FieldErrors<ProjectDataInput>) {
  const out: { field: string; message: string }[] = [];
  for (const [k, v] of Object.entries(errors)) {
    const msg = (v as { message?: string } | undefined)?.message;
    if (msg) out.push({ field: k, message: msg });
  }
  return out;
}

interface PrefillBanner {
  sourceKind: SourceKind;
  filled: number;
  skipped: number;
  warnings: string[];
}

function PrefillSummary({
  banner,
  onDismiss,
}: {
  banner: PrefillBanner;
  onDismiss: () => void;
}) {
  const sourceLabel: Record<SourceKind, string> = {
    dxf: "iz DXF/DWG datoteke",
    ifc: "iz IFC modela",
    nl: "iz opisa (glas/tekst/dokument)",
  };
  return (
    <div className="mt-4 flex items-start gap-2 rounded-md border border-ind-200 bg-ind-50 px-4 py-3 text-[0.85rem] text-ind-800">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1">
        <div className="font-semibold">
          Popunjeno {sourceLabel[banner.sourceKind]} — {banner.filled}{" "}
          {banner.filled === 1 ? "polje" : "polja"}
          {banner.skipped > 0 ? (
            <>
              {" "}(preskočeno {banner.skipped} jer ih je već popunio nacrt)
            </>
          ) : null}
          .
        </div>
        <div className="mt-1 text-[0.8rem]">
          Polja sa žutom oznakom imaju pouzdanost ispod 75 % — provjeri ih
          prije generiranja dokumentacije.
        </div>
        {banner.warnings.length > 0 && (
          <ul className="mt-2 list-disc pl-5 text-[0.78rem]">
            {banner.warnings.slice(0, 5).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
        Zatvori
      </Button>
    </div>
  );
}
